import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import { useAnnounce } from '@/components/ui/live-region';
import { VisuallyHidden } from '@/components/ui/visually-hidden';

interface ErrorFallbackProps {
  error?: Error;
  resetError: () => void;
}

export const ErrorFallback: React.FC<ErrorFallbackProps> = ({
  error,
  resetError
}) => {
  const { announce } = useAnnounce();

  const handleReload = () => {
    announce('Reloading page...', 'assertive');
    window.location.reload();
  };

  const goHome = () => {
    announce('Navigating to home page...', 'assertive');
    window.location.href = '/';
  };

  const handleTryAgain = () => {
    announce('Attempting to recover from error...', 'assertive');
    resetError();
  };

  // Announce error to screen readers
  React.useEffect(() => {
    announce('An error has occurred. Please try again or contact support.', 'assertive');
  }, [announce]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 p-3 bg-destructive/10 rounded-full w-fit">
            <AlertCircle className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle className="text-xl text-destructive">Something went wrong</CardTitle>
        </CardHeader>
        <CardContent className="text-center space-y-4">
          <p className="text-muted-foreground">
            We encountered an unexpected error. This has been logged and we'll look into it.
          </p>

          {process.env.NODE_ENV === 'development' && error && (
            <details className="text-left bg-muted p-3 rounded-md text-sm">
              <summary className="cursor-pointer font-medium mb-2">Error Details</summary>
              <pre className="whitespace-pre-wrap text-xs overflow-auto">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              onClick={handleTryAgain}
              variant="outline"
              className="flex items-center gap-2"
              aria-describedby="try-again-description"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Try Again
              <VisuallyHidden id="try-again-description">
                Attempt to recover from the error without reloading the page
              </VisuallyHidden>
            </Button>
            <Button
              onClick={handleReload}
              variant="outline"
              className="flex items-center gap-2"
              aria-describedby="reload-description"
            >
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Reload Page
              <VisuallyHidden id="reload-description">
                Reload the entire page to start fresh
              </VisuallyHidden>
            </Button>
            <Button
              onClick={goHome}
              className="flex items-center gap-2"
              aria-describedby="home-description"
            >
              <Home className="h-4 w-4" aria-hidden="true" />
              Go Home
              <VisuallyHidden id="home-description">
                Navigate to the home page
              </VisuallyHidden>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
